import React, { useEffect, useState, lazy } from 'react';
import { HeartIcon, ShieldIcon, SparklesIcon, LeafIcon, BrainIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function GutBenefitsSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const headerReveal = useScrollReveal({
    threshold: 0.2
  });
  const imageReveal = useScrollReveal({
    threshold: 0.2
  });
  const benefitsReveal = useScrollReveal({
    threshold: 0.1
  });
  const benefits = [{
    icon: <ShieldIcon className="w-6 h-6" data-id="element-171" />,
    title: '65% Boswellic Acids',
    description: "Most frankincense supplements contain lower concentrations. Ours contains 65% boswellic acids — one of the highest concentrations available. That's the difference between a token dose and real comfort.*"
  }, {
    icon: <BrainIcon className="w-6 h-6" data-id="element-172" />,
    title: 'Three Compounds. One System.',
    description: 'AKBA, KBA & acetyl-boswellic acids work together to support the comfort-mobility-ease system that breaks down when your joints feel restricted.'
  }, {
    icon: <LeafIcon className="w-6 h-6" data-id="element-173" />,
    title: 'No Fillers. No Binders. No Junk.',
    description: 'Pure frankincense extract. No magnesium stearate, no synthetic fillers, no questionable additives. Just what God created — nothing else.'
  }, {
    icon: <SparklesIcon className="w-6 h-6" data-id="element-174" />,
    title: 'Whole-Body Protection',
    description: 'Clinical research shows frankincense supports joint health, inflammation response, mobility, and overall comfort — protecting your entire body at the cellular level.*'
  }];
  // Auto-advance slideshow
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % benefits.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, benefits.length]);
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % benefits.length);
    setIsAutoPlaying(false);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + benefits.length) % benefits.length);
    setIsAutoPlaying(false);
  };
  return <section className="w-full bg-[#f5f1ed] py-16 lg:py-24" data-id="element-175">
      <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-176">
        {/* Section Header */}
        <div ref={headerReveal.ref} className={`text-center mb-12 transition-all duration-1000 ${headerReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-177">
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#2d4a3a] mb-4" data-id="element-178">
            God Already Created the Answer.
          </h2>
          <p className="text-gray-600 text-xs max-w-2xl mx-auto mb-6" data-id="element-179">
            The biblical botanical with active compounds clinically shown to
            support joint comfort, daily mobility & whole-body ease starting
            within your first week.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center" data-id="element-180">
          {/* Left: Image */}
          <div ref={imageReveal.ref} className={`order-1 lg:order-1 transition-all duration-1000 ${imageReveal.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`} data-id="element-181">
            <div className="relative" data-id="element-182">
              <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_24_Frame17393323731_3356911832620697_-_Copy.png?v=1763935221" alt="Faith & Fig Divine Wellness Product" className="w-full max-w-md mx-auto rounded-2xl shadow-xl transform hover:scale-105 transition-transform duration-300" data-id="element-183" />
              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-[#496f58] text-white rounded-full w-24 h-24 flex flex-col items-center justify-center shadow-xl animate-bounce" data-id="element-184">
                <span className="text-2xl font-bold" data-id="element-185">90%</span>
                <span className="text-[8px] text-center leading-tight" data-id="element-186">
                  Feel Better
                </span>
              </div>
            </div>
          </div>

          {/* Right: Slideshow */}
          <div ref={benefitsReveal.ref} className="order-2 lg:order-2" data-id="element-187">
            {/* Slideshow Container */}
            <div className="relative min-h-[140px] mb-4" data-id="element-188">
              {benefits.map((benefit, index) => <div key={index} className={`transition-all duration-700 ${index === currentSlide ? 'opacity-100 translate-x-0 relative' : 'opacity-0 translate-x-8 absolute inset-0 pointer-events-none'}`} data-id="element-189">
                  <div className="flex items-start gap-4 group" data-id="element-190">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#496f58] text-white flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-191">
                      {benefit.icon}
                    </div>
                    <div data-id="element-192">
                      <h3 className="font-bold text-lg text-[#2d4a3a] mb-2" data-id="element-193">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed" data-id="element-194">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}